/* ==========================================================================
   ProTrade Commerce — slider.js
   Scroll-snap carousel: arrows, dots, autoplay, drag, keyboard, a11y.
   ========================================================================== */
(function () {
  'use strict';

  const T = window.theme;
  const { $, $$, throttle } = T.utils;

  class Slider {
    constructor(root) {
      this.root = root;
      this.track = $('[data-slider-track]', root);
      if (!this.track) return;

      this.slides = $$('[data-slider-slide]', this.track);
      this.prev = $('[data-slider-prev]', root);
      this.next = $('[data-slider-next]', root);
      this.dotsWrapper = $('[data-slider-dots]', root);

      this.autoplay = root.dataset.autoplay === 'true';
      this.autoplaySpeed = parseInt(root.dataset.autoplaySpeed || '6', 10) * 1000;
      this.loop = root.dataset.loop !== 'false';
      this.index = 0;
      this.timer = null;

      if (this.slides.length <= 1) {
        this.prev && (this.prev.hidden = true);
        this.next && (this.next.hidden = true);
        this.dotsWrapper && (this.dotsWrapper.hidden = true);
        return;
      }

      this.buildDots();
      this.bind();
      this.update();
      if (this.autoplay) this.start();
    }

    /* --------------------------------------------------------- geometry */

    get perView() {
      const first = this.slides[0];
      if (!first) return 1;
      const gap = parseFloat(getComputedStyle(this.track).columnGap || '0') || 0;
      return Math.max(1, Math.round(this.track.clientWidth / (first.offsetWidth + gap)));
    }

    get pageCount() {
      return Math.max(1, this.slides.length - this.perView + 1);
    }

    /* ------------------------------------------------------------- dots */

    buildDots() {
      if (!this.dotsWrapper) return;
      this.dotsWrapper.innerHTML = '';
      const count = this.root.dataset.dotsPerSlide === 'true' ? this.slides.length : this.pageCount;

      for (let i = 0; i < count; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'slider__dot';
        dot.setAttribute('aria-label', `${i + 1}`);
        dot.addEventListener('click', () => this.goTo(i));
        this.dotsWrapper.appendChild(dot);
      }
      this.dots = $$('.slider__dot', this.dotsWrapper);
    }

    /* ----------------------------------------------------------- events */

    bind() {
      this.prev && this.prev.addEventListener('click', () => { this.stop(); this.goTo(this.index - 1); });
      this.next && this.next.addEventListener('click', () => { this.stop(); this.goTo(this.index + 1); });

      this.track.addEventListener('scroll', throttle(() => this.syncFromScroll(), 90), { passive: true });

      this.root.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.stop(); this.goTo(this.index - 1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); this.stop(); this.goTo(this.index + 1); }
      });

      this.root.addEventListener('mouseenter', () => this.pause());
      this.root.addEventListener('mouseleave', () => { if (this.autoplay && !this.stopped) this.start(); });
      this.root.addEventListener('focusin', () => this.pause());

      window.addEventListener('resize', throttle(() => { this.buildDots(); this.update(); }, 200));

      this.bindDrag();
    }

    bindDrag() {
      let startX = 0;
      let startScroll = 0;
      let dragging = false;

      const down = (event) => {
        if (event.button !== undefined && event.button !== 0) return;
        dragging = true;
        startX = event.pageX;
        startScroll = this.track.scrollLeft;
        this.track.style.scrollBehavior = 'auto';
        this.track.style.cursor = 'grabbing';
      };

      const move = (event) => {
        if (!dragging) return;
        const delta = event.pageX - startX;
        if (Math.abs(delta) > 4) event.preventDefault();
        this.track.scrollLeft = startScroll - delta;
      };

      const up = () => {
        if (!dragging) return;
        dragging = false;
        this.track.style.scrollBehavior = '';
        this.track.style.cursor = '';
        this.syncFromScroll();
      };

      this.track.addEventListener('mousedown', down);
      this.track.addEventListener('mousemove', move);
      window.addEventListener('mouseup', up);
      this.track.addEventListener('mouseleave', up);
    }

    /* -------------------------------------------------------- navigation */

    goTo(index) {
      const max = this.pageCount - 1;
      if (index < 0) index = this.loop ? max : 0;
      if (index > max) index = this.loop ? 0 : max;

      this.index = index;
      const slide = this.slides[index];
      if (slide) {
        this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
      }
      this.update();
    }

    syncFromScroll() {
      const positions = this.slides.map((slide) => Math.abs(slide.offsetLeft - this.track.offsetLeft - this.track.scrollLeft));
      const nearest = positions.indexOf(Math.min.apply(null, positions));
      this.index = Math.min(nearest, this.pageCount - 1);
      this.update();
    }

    update() {
      if (this.dots) {
        this.dots.forEach((dot, i) => {
          const active = i === this.index;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-current', active ? 'true' : 'false');
        });
      }

      if (!this.loop) {
        this.prev && (this.prev.disabled = this.index === 0);
        this.next && (this.next.disabled = this.index >= this.pageCount - 1);
      }

      this.slides.forEach((slide, i) => {
        const visible = i >= this.index && i < this.index + this.perView;
        slide.setAttribute('aria-hidden', String(!visible));
        $$('a, button, input', slide).forEach((el) => {
          if (visible) el.removeAttribute('tabindex');
          else el.setAttribute('tabindex', '-1');
        });
      });

      this.root.dispatchEvent(new CustomEvent('slider:change', { detail: { index: this.index } }));
    }

    /* ---------------------------------------------------------- autoplay */

    start() {
      if (!this.autoplay) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.stop();
      this.stopped = false;
      this.timer = setInterval(() => this.goTo(this.index + 1), this.autoplaySpeed);
    }

    pause() { clearInterval(this.timer); }

    stop() {
      clearInterval(this.timer);
      this.timer = null;
      this.stopped = true;
    }
  }

  function initSliders(scope) {
    $$('[data-slider]', scope || document).forEach((root) => {
      if (root._slider) return;
      root._slider = new Slider(root);
    });
  }

  T.initSliders = initSliders;
  T.Slider = Slider;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initSliders());
  } else {
    initSliders();
  }

  document.addEventListener('theme:section:load', (event) => initSliders(event.detail.target));
})();
